import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useParams, useLocation } from "react-router-dom";

import Header from "./components/Header";
import Footer from "./components/Footer";
import ShoppingFilter from "./components/ShoppingFilter";
import ProductDisplay from "./components/ProductDisplay";
import "../css/shopping.css";

const ShoppingPage = () => {
  const { brandName } = useParams(); // Access the brand name from the URL
  const location = useLocation(); // Access query parameters

  const hasLoadedScripts = useRef(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [brandData, setBrandData] = useState({});
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);

  const Head = () => {
    return (
      <Helmet>
        <title>{`STAR TECH | BROWSE`}</title>
      </Helmet>
    );
  };

  useEffect(() => {
    const loadScript = (src) => {
      return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = src;
        script.async = true;
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
      });
    };

    const scripts = [
      // Add other script paths here...
      "/js/jquery.js",
      "/js/popper.min.js",
      "/js/bootstrap.min.js",
      "/js/jquery.fancybox.js",
      "/js/jquery-ui.js",
      "/js/jquery.countdown.js",
      "/js/mixitup.js",
      "/js/wow.js",
      "/js/appear.js",
      "/js/select2.min.js",
      "/js/swiper.min.js",
      "/js/owl.js",
      "/js/script.js",
      "/js/st-products-custom.js",
    ];

    const loadScripts = async () => {
      if (!hasLoadedScripts.current) {
        try {
          for (const script of scripts) {
            await loadScript(script);
          }
          hasLoadedScripts.current = true; // Mark as loaded
          console.log("All scripts loaded successfully");
        } catch (error) {
          console.error("Error loading scripts", error);
        }
      }
    };

    loadScripts();
    setTimeout(() => {
      setIsLoaded(true);
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }, 1500);

    const fetchData = async () => {
      try {
        const response = await fetch("/products.json");
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const data = await response.json();
        console.log(data.brands);
        const brand = data.brands.find((brand) => brand.name === brandName);
        console.log(brand);
        setBrandData(brand);
        setCategories(data.categories || []);

        // Get query parameters
        const searchParams = new URLSearchParams(location.search);
        const categoryParam = searchParams.get('category');

        // Set selected brand if brandName is provided in URL
        if (brandName && brand) {
          setSelectedBrand(brand.id);
        }

        // Set selected category if category parameter is provided in URL
        if (categoryParam) {
          // Check if the category exists in the data
          const categoryExists = data.categories.some(
            (cat) => cat.class === categoryParam || cat.name.toLowerCase() === categoryParam.toLowerCase()
          );
          if (categoryExists) {
            // Find the category class by name or use the parameter directly
            const category = data.categories.find(
              (cat) => cat.class === categoryParam || cat.name.toLowerCase() === categoryParam.toLowerCase()
            );
            setSelectedCategory(category ? category.class : categoryParam);
          }
        }

        // Calculate product count for each brand
        const brandsWithProductCount = data.brands.map((brand) => {
          const productCount = data.products.filter(
            (product) => product.brand_id === brand.id
          ).length;
          return {
            ...brand,
            products: { length: productCount },
          };
        });

        setBrands(brandsWithProductCount);
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    };

    fetchData();
  }, [brandName, location.search]);

  return (
    <>
      <Head />
      <div className="page-wrapper">
        {/* <!-- Preloader --> */}
        {!isLoaded && <div className="preloader"></div>}
        <Header selection="products" />
        {/* <!-- Start main-content --> */}
        <section
          className="page-title"
          style={{
            backgroundImage: "url(../images/background/page-title.jpg)",
          }}
        >
          <div className="auto-container">
            <div className="title-outer">
              <h1 className="title">Browse Products</h1>
            </div>
          </div>
        </section>
        <section>
          <div className="auto-container">
            <div className="title-outer">
              <ul className="page-breadcrumb">
                <li>
                  <Link to={"/"}>Home</Link>
                </li>
                <li>
                  <Link to={"/brands"}>Brands</Link>
                </li>
                <li>{brandName}</li>
              </ul>
            </div>
            {/* <div className='text-center'>
              <img
                src={`./../../images/brands/${brandName}.png`}
                alt={brandName}
                style={{height: '120px'}}
                className='mt-5'
              />
            </div>
            <br />
            <br />
            <p>
              {brandData?.description || ''}
            </p> */}
          </div>
        </section>
        {/* <!-- end main-content --> */}
        {/* <!-- Featured Products --> */}
        <section className="" style={{padding: '30px'}}>
          <span className="bg-shape"></span>
          <div className="auto-container">
            <div className="row clearfix">
              {/* Filters - Left Side */}
              <div className="col-lg-3 col-md-4 col-sm-12 sidebar-side">
                <div className="shop-sidebar">
                  <ShoppingFilter
                    brandName={brandName}
                    onCategoryChange={setSelectedCategory}
                    onBrandChange={setSelectedBrand}
                    selectedCategory={selectedCategory}
                    selectedBrand={selectedBrand}
                  />
                </div>
              </div>

              {/* Products Display - Right Side */}
              <div className="col-lg-9 col-md-8 col-sm-12 content-side">
                {/* Search and Filter Bar */}
                <div className="search-filter-bar">
                  <div className="search-container">
                    <div className="search-input-wrapper">
                      <i className="fas fa-search search-icon"></i>
                      <input
                        type="text"
                        className="search-input"
                        placeholder="Search products..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                      {searchQuery && (
                        <button
                          className="clear-search"
                          onClick={() => setSearchQuery("")}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Active Filters */}
                  <div className="active-filters">
                    {selectedCategory !== "all" && (
                      <div className="filter-chip">
                        <span>
                          Category:{" "}
                          {categories.find(
                            (cat) => cat.class === selectedCategory
                          )?.name || selectedCategory}
                        </span>
                        <button onClick={() => setSelectedCategory("all")}>
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    )}
                    {selectedBrand && (
                      <div className="filter-chip">
                        <span>
                          Brand:{" "}
                          {brands.find((brand) => brand.id === selectedBrand)
                            ?.name || "Unknown"}
                        </span>
                        <button onClick={() => setSelectedBrand(null)}>
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    )}
                    {searchQuery && (
                      <div className="filter-chip">
                        <span>Search: "{searchQuery}"</span>
                        <button onClick={() => setSearchQuery("")}>
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mixitup-gallery">
                  <div id="products-display" className="filter-list row">
                    <ProductDisplay
                      selectedCategory={selectedCategory}
                      selectedBrand={selectedBrand}
                      searchQuery={searchQuery}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* <!-- End Featured Products --> */}

        <Footer />
      </div>
      {/* <!-- Scroll To Top --> */}
      <div className="scroll-to-top scroll-to-target" data-target="html">
        <span className="fa fa-angle-up"></span>
      </div>
    </>
  );
};

export default ShoppingPage;
